import { Store } from '@dojo/framework/stores/Store';
import { createProcess, createCommandFactory } from '@dojo/framework/stores/process';
import { add, replace } from '@dojo/framework/stores/state/operations';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Process, ProcessResult } from '@dojo/framework/stores/process';

import { createStoreMiddleware } from '@holmescorp/middleware';

import { uuidv4 } from '@holmescorp/uuid';

import { QuizMeta, QuizRPC, InfoRPC, Question } from '../interfaces';
import { logQuiz } from '@holmescorp/logger';
import { QuizSectionsMessage, ActiveQuizSection } from '../quizsection';

export const quizstores = new Map<string, Store<QuizMeta>>();
export const quizstate = createStoreMiddleware<QuizMeta>();

const createCommand = createCommandFactory<any, QuizMeta>();

// -----
// Set up the initial values in the store.
const initialStateCommand = createCommand<any>(({ path }) => {
	return [
		add(path('quizurl'), ''),
		add(path('quizid'), ''),
		add(path('quiz'), undefined),
		add(path('info'), undefined),
		add(path('status'), undefined),
		add(path('question'), undefined),
		add(path('progression'), undefined),
		add(path('progressionWidget'), undefined),
		add(path('inreview'), false),
		add(path('isaudit'), false),
		add(path('countdown'), ''),
		add(path('progressionState', 'isrunning'), ''),
		add(path('instancekey'), uuidv4()),
		add(path('hasqueue'), false),
		add(path('quizSections'), undefined),
		add(path('isautostart'), false),
		add(path('activeSection'), { cursor: -1, section: undefined })
	];
});

const resetStateCommand = createCommand<any>(({ path }) => {
	return [
		replace(path('quizurl'), ''),
		replace(path('quizid'), ''),
		replace(path('quiz'), undefined),
		replace(path('info'), undefined),
		replace(path('status'), undefined),
		replace(path('question'), undefined),
		replace(path('progression'), undefined),
		replace(path('progressionWidget'), undefined),
		replace(path('inreview'), false),
		replace(path('isaudit'), false),
		replace(path('countdown'), ''),
		replace(path('progressionState', 'isrunning'), ''),
		replace(path('instancekey'), uuidv4()),
		replace(path('hasqueue'), false),
		replace(path('stopLoading'), undefined),
		replace(path('quizSections'), undefined),
		replace(path('isautostart'), false),
		add(path('activeSection'), { cursor: -1, section: undefined })
	];
});

const initialStateProcess = createProcess('quiz-initial-state', [initialStateCommand]);
const resetStateProcess = createProcess('quiz-reset-state', [resetStateCommand]);
export const initialState = (quizid: string, store: Store<QuizMeta>): void => {
	const { path, get } = store;
	if (get(path('instancekey')) === undefined) {
		initialStateProcess(store)({});
	} else {
		resetStateProcess(store)({});
	}
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	quizidChange(store)({ quizid });
};

// -----
const quizidCommand = createCommand<{ quizid: string }>(({ path, payload: { quizid } }) => {
	const quizurl = `${window.hcstatic.portal_url}/quiz/${quizid}/`;
	return [replace(path('quizid'), quizid), replace(path('quizurl'), quizurl)];
});

const quizidChangeProcess = createProcess('quizid-change', [quizidCommand]);
export const quizidChange = quizidChangeProcess;

// -----
const quizCommand = createCommand<{ quiz: QuizRPC }>(({ path, payload: { quiz } }) => {
	return [replace(path('quiz'), quiz)];
});

const quizChangeProcess = createProcess('quiz-change', [quizCommand]);
export const quizChange = quizChangeProcess;

// -----
const infoCommand = createCommand<{ info: InfoRPC }>(({ path, payload: { info } }) => {
	return [
		replace(path('info'), info),
		replace(path('status'), info.start_state),
		replace(path('cursor'), info.currentpage - 1)
	];
});

const infoChangeProcess = createProcess('info-change', [infoCommand]);
export const infoChange = infoChangeProcess;

// -----
const titleCommand = createCommand<{ title: string }>(({ path, payload: { title } }) => {
	return [replace(path('title'), title)];
});

const titleProcess = createProcess('quiz-title', [titleCommand]);
export const setTitle = titleProcess;

// -----
// Manage the question cursor
const cursorCommand = createCommand<{ cursor: number }>(({ path, payload: { cursor } }) => {
	return [replace(path('cursor'), cursor)];
});

const cursorIncrCommand = createCommand<any>(({ path, get }) => {
	const { numberofquestions, lastresult } = get(path('info'));
	const isaudit = get(path('isaudit'));
	const totalquestions = isaudit ? lastresult.numberofquestions : numberofquestions;
	const cursor = get(path('cursor'));
	const maxcursor = totalquestions - 1;
	return [replace(path('cursor'), cursor < maxcursor ? cursor + 1 : cursor)];
});

const cursorDecrCommand = createCommand<any>(({ path, get }) => {
	const cursor = get(path('cursor'));
	return [replace(path('cursor'), cursor > 0 ? cursor - 1 : cursor)];
});

const cursorProcess = createProcess('quiz-cursor', [cursorCommand]);
const incrCursorProcess = createProcess('quiz-incr-cursor', [cursorIncrCommand]);
const decrCursorProcess = createProcess('quiz-decr-cursor', [cursorDecrCommand]);
export const setCursor = cursorProcess;
export const incrCursor = (store: Store<QuizMeta>): Promise<ProcessResult<any, any>> => incrCursorProcess(store)({});
export const decrCursor = (store: Store<QuizMeta>): Promise<ProcessResult<any, any>> => decrCursorProcess(store)({});

// -----
const setQuestionCommand = createCommand<{ question: Question | undefined }>(({ get, path, payload: { question } }) => {
	const currq = get(path('question'));
	const logit = (currq === undefined && question !== undefined) || (currq && question && currq.id !== question.id);
	logit && logQuiz.info(() => `question ${question!.id}`);
	return [replace(path('question'), question)];
});

const setQuestionProcess = createProcess('set-question', [setQuestionCommand]);
export const setQuestion = setQuestionProcess;

// -----
const setProgressionCommand = createCommand<{ progression: string }>(({ path, payload: { progression } }) => {
	return [replace(path('progression'), progression)];
});

const setProgressionProcess = createProcess('set-progression', [setProgressionCommand]);
export const setProgression = setProgressionProcess;

// -----
const setProgressionWidgetCommand = createCommand<{ progressionWidget: string }>(
	({ path, payload: { progressionWidget } }) => {
		return [replace(path('progressionWidget'), progressionWidget)];
	}
);

const setProgressionWidgetProcess = createProcess('set-progression-widget', [setProgressionWidgetCommand]);
export const setProgressionWidget = setProgressionWidgetProcess;

// -----
const setLoadedCommand = createCommand<{ loaded: boolean }>(({ path, payload: { loaded } }) => {
	return [replace(path('loaded'), loaded)];
});

const setLoadedProcess = createProcess('set-loaded', [setLoadedCommand]);
export const setLoaded = setLoadedProcess;

// -----
const setInreviewCommand = createCommand<{ inreview: boolean }>(({ path, payload: { inreview } }) => {
	return [replace(path('inreview'), inreview)];
});

const setInreviewProcess = createProcess('set-inreview', [setInreviewCommand]);
export const setInreview = setInreviewProcess;

// -----
const setIsauditCommand = createCommand<{ isaudit: boolean }>(({ path, payload: { isaudit } }) => {
	return [replace(path('isaudit'), isaudit)];
});

const setIsauditProcess = createProcess('set-isaudit', [setIsauditCommand]);
export const setIsaudit = setIsauditProcess;

// -----
const setCountdownCommand = createCommand<{ countdown: string }>(({ path, payload: { countdown } }) => {
	return [replace(path('countdown'), countdown)];
});

const setCountdownProcess = createProcess('set-countdown', [setCountdownCommand]);
export const setCountdown = setCountdownProcess;

// -----
const setStopLoadingCommand = createCommand<{ stopLoading: (() => void) | undefined }>(
	({ path, payload: { stopLoading } }) => {
		return [replace(path('stopLoading'), stopLoading)];
	}
);

const setStopLoadingProcess = createProcess('set-stoploading', [setStopLoadingCommand]);
export const setStopLoading = setStopLoadingProcess;

// -----
const setHasqueueCommand = createCommand<{ hasqueue: boolean }>(({ path, payload: { hasqueue } }) => {
	return [replace(path('hasqueue'), hasqueue)];
});

const setHasqueueProcess = createProcess('set-hasqueue', [setHasqueueCommand]);
export const setHasQueue = setHasqueueProcess;

// -----
// progression state
const setIsrunningCommand = createCommand<{ isrunning: string }>(({ path, payload: { isrunning } }) => {
	return [replace(path('progressionState', 'isrunning'), isrunning)];
});

const setIsrunningProcess = createProcess('set-isrunning', [setIsrunningCommand]);
export const setProgressionIsrunning = setIsrunningProcess;

// load quiz combined process
export const loadQuizProcess = createProcess<
	any,
	{ title: string; question: Question | undefined; quiz: QuizRPC; info: InfoRPC }
>('load-quiz', [titleCommand, setQuestionCommand, quizCommand, infoCommand]);

//
const setQuizSectionsCommand = createCommand<{ quizSections: QuizSectionsMessage }>(
	({ path, payload: { quizSections } }) => {
		return [replace(path('quizSections'), quizSections)];
	}
);

export const setQuizSectionsProcess = createProcess('set-quiz-sections', [setQuizSectionsCommand]);
export const setQuizSections = setQuizSectionsProcess;

//
const setActiveQuizSectionCommand = createCommand<ActiveQuizSection>(
	({ path, payload: { cursor, timeout, section } }) => {
		return [replace(path('activeSection'), { cursor, timeout, section })];
	}
);

export const setActiveQuizSectionProcess = createProcess('set-active-quiz-section', [setActiveQuizSectionCommand]);
export const setActiveQuizSection = setActiveQuizSectionProcess;

const setIsAutoStartCommand = createCommand<{ isautostart: boolean }>(({ path, payload: { isautostart } }) => {
	return [replace(path('isautostart'), isautostart)];
});

export const setIsAutoStartProcess = createProcess('set-quiz-auto-start', [setIsAutoStartCommand]);
export const setIsAutoStart = setIsAutoStartProcess;
